import React, { useState } from 'react';
import { FaCalendarAlt, FaClock, FaCheckCircle, FaChevronRight, FaChevronLeft, FaInfoCircle } from 'react-icons/fa';
import styles from '../../pages/Account.module.css';
import { toast } from 'react-toastify';

const TabBooking = ({ user }) => {
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [step, setStep] = useState(1); // 1: Date, 2: Time, 3: Confirm
    const [bookingType, setBookingType] = useState('visite'); // 'visite', 'conseil', 'technique'

    // Simplified calendar logic for demo
    const today = new Date();
    const next14Days = Array.from({ length: 14 }, (_, i) => {
        const date = new Date();
        date.setDate(today.getDate() + i + 1);
        return date;
    });

    const timeSlots = [
        '09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00'
    ];

    const handleDateSelect = (date) => {
        setSelectedDate(date);
        setStep(2);
    };

    const handleTimeSelect = (time) => {
        setSelectedTime(time);
        setStep(3);
    };

    const handleConfirm = () => {
        toast.success('Rendez-vous réservé avec succès !');
        // In a real app, send to PB
        setStep(4); // Success state
    };

    const formatDate = (date) => {
        if (!date) return '';
        return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    };

    const types = [
        { id: 'visite', label: 'Visite de Chantier', icon: '🏗️', description: 'Visite guidée de votre maison en cours de construction.' },
        { id: 'conseil', label: 'Conseil Design', icon: '🎨', description: 'Choix des matériaux, finitions et aménagement.' },
        { id: 'technique', label: 'Point Technique', icon: '🔧', description: 'Réunion avec le chef de chantier pour les aspects techniques.' },
    ];

    return (
        <div className={styles.tabContent}>
            <div className={styles.sectionHeader}>
                <div>
                    <h2 className={styles.contentTitle}>Prendre Rendez-vous</h2>
                    <p style={{ margin: 0, opacity: 0.7, fontSize: '0.9rem' }}>Planifiez votre prochaine visite ou réunion avec notre équipe.</p>
                </div>
            </div>

            {step === 1 && (
                <div className={styles.bookingContainer}>
                    <div className={styles.bookingTypeGrid}>
                        {types.map(t => (
                            <div
                                key={t.id}
                                className={`${styles.bookingTypeCard} ${bookingType === t.id ? styles.activeType : ''}`}
                                onClick={() => setBookingType(t.id)}
                            >
                                <span className={styles.typeIcon}>{t.icon}</span>
                                <h4>{t.label}</h4>
                                <p>{t.description}</p>
                            </div>
                        ))}
                    </div>

                    <h3 className={styles.subtitle} style={{ marginTop: '2rem' }}>Sélectionnez une date</h3>
                    <div className={styles.dateGrid}>
                        {next14Days.map((date, i) => {
                            const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                            return (
                                <button
                                    key={i}
                                    className={`${styles.dateCard} ${isWeekend ? styles.disabledDate : ''}`}
                                    disabled={isWeekend}
                                    onClick={() => handleDateSelect(date)}
                                >
                                    <span className={styles.monthName}>
                                        {date.toLocaleDateString('fr-FR', { month: 'short' }).toUpperCase()}
                                    </span>
                                    <span className={styles.dayNum}>{date.getDate()}</span>
                                    <span className={styles.dayName}>
                                        {date.toLocaleDateString('fr-FR', { weekday: 'short' })}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {step === 2 && (
                <div className={styles.bookingContainer}>
                    <button className={styles.backLink} onClick={() => setStep(1)}>
                        <FaChevronLeft /> Retour aux dates
                    </button>
                    <div className={styles.selectionSummary}>
                        <FaCalendarAlt /> {formatDate(selectedDate)}
                    </div>

                    <h3 className={styles.subtitle}>Choisissez un horaire</h3>
                    <div className={styles.timeGrid}>
                        {timeSlots.map(time => (
                            <button
                                key={time}
                                className={styles.timeSlot}
                                onClick={() => handleTimeSelect(time)}
                            >
                                <FaClock /> {time}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {step === 3 && (
                <div className={styles.bookingContainer}>
                    <button className={styles.backLink} onClick={() => setStep(2)}>
                        <FaChevronLeft /> Retour aux horaires
                    </button>

                    <div className={styles.confirmCard}>
                        <h3>Confirmer votre rendez-vous</h3>
                        <div className={styles.confirmInfo}>
                            <div className={styles.confirmItem}>
                                <label>Type</label>
                                <span>{types.find(t => t.id === bookingType).label}</span>
                            </div>
                            <div className={styles.confirmItem}>
                                <label>Date</label>
                                <span>{formatDate(selectedDate)}</span>
                            </div>
                            <div className={styles.confirmItem}>
                                <label>Heure</label>
                                <span>{selectedTime}</span>
                            </div>
                            <div className={styles.confirmItem}>
                                <label>Client</label>
                                <span>{user?.name || user?.email}</span>
                            </div>
                        </div>

                        <div className={styles.notice}>
                            <FaInfoCircle />
                            <p>Une confirmation vous sera envoyée par email. Vous pourrez annuler ou modifier ce rendez-vous jusqu'à 24h avant.</p>
                        </div>

                        <button className={styles.submitButton} onClick={handleConfirm} style={{ width: '100%', marginTop: '20px' }}>
                            Confirmer la réservation
                        </button>
                    </div>
                </div>
            )}

            {step === 4 && (
                <div className={styles.successState}>
                    <div className={styles.successIcon}>
                        <FaCheckCircle />
                    </div>
                    <h2>Rendez-vous Confirmé !</h2>
                    <p>Votre demande a été enregistrée. Un membre de notre équipe reviendra vers vous si nécessaire.</p>
                    <div className={styles.summaryBadge}>
                        <strong>{types.find(t => t.id === bookingType).label}</strong><br />
                        {formatDate(selectedDate)} à {selectedTime}
                    </div>
                    <button className={styles.outlineButton} onClick={() => setStep(1)} style={{ marginTop: '30px' }}>
                        Prendre un autre rendez-vous
                    </button>
                </div>
            )}
        </div>
    );
};

export default TabBooking;
